import * as React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Model } from 'survey-core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { IServerTokens } from '@pec/aion-ui-core/interfaces/serverTokens';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { createCompanyWithoutPayment, fetchServerTokens } from '../helpers/apiCalls';
import { getAccount } from '../helpers/registerSurveyCallbacks';
import { updateLead } from '../helpers/events';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonContainer: {
      minWidth: 278,
      [theme.breakpoints.down('md')]: {
        minWidth: 'initial'
      }
    }
  })
);

type Props = {
  survey: Model;
};
export const Navigation: React.FC<Props> = ({ survey }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [pageNo, setPageNo] = React.useState(survey.currentPageNo);
  survey.onCurrentPageChanged.add((_, options) => {
    setPageNo(options.newCurrentPage.visibleIndex);
  });
  const isAdmin = survey.getVariable('isAdmin');
  const isLastPage = pageNo === survey.visiblePages.length - 1;
  const prevPage = () => survey.prevPage();
  const nextPage = () => survey.nextPage();
  const endSurvey = () => survey.completeLastPage();

  const fetchServerTokensData = async () => {
    try {
      const response = await fetchServerTokens();
      return response.data;
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const redirectTo = (status: number) => {
    const email = survey.data.email;
    const userExists = !!survey.getVariable('userExists');
    const successLink = `/signup/success?email=${encodeURIComponent(
      email
    )}&userExists=${userExists}&withoutPayment=true`;
    const failureLink = '/signup/failure';

    window.location.assign(status === 200 ? successLink : failureLink);
  };

  const createCompanyWithoutPaymentRequest = async (serverTokens: IServerTokens) => {
    try {
      const accountId = getAccount();
      if (accountId) {
        await updateLead(survey, survey.data, survey.currentPage.num, accountId);
        const response = await createCompanyWithoutPayment(accountId, serverTokens);
        redirectTo(response.status);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCompanyWithoutPayment = async () => {
    survey.validateCurrentPage();
    if (!survey.isCurrentPageValid) return;
    setIsLoading(true);
    const { serverTokens } = await fetchServerTokensData();
    if (serverTokens) {
      await createCompanyWithoutPaymentRequest(serverTokens);
    }
  };

  const renderButton = (text: string, func: () => void, canRender: boolean, key: string): JSX.Element | null => {
    if (!canRender) return null;
    return (
      <Box className="sv-action">
        <button className={`sd-btn sd-navigation__${key}-btn`} onClick={func} tabIndex={1}>
          {text}
        </button>
      </Box>
    );
  };

  if (isLoading) return <Loading />;

  return (
    <div className={clsx('sd-action-bar', isAdmin && isLastPage && 'sd-action-admin')}>
      <Box display="flex" className={classes.buttonContainer}>
        {renderButton(survey.pagePrevText, prevPage, pageNo !== 0, 'prev')}
        {renderButton(survey.pageNextText, nextPage, pageNo !== survey.visiblePages.length - 1, 'next')}
      </Box>
      {isAdmin && (
        <>
          {renderButton(survey.completeText, endSurvey, isLastPage, 'complete')}
          {renderButton(
            t('navigation.createWithoutPayment', 'Create company without payment'),
            handleCreateCompanyWithoutPayment,
            isLastPage,
            'complete'
          )}
        </>
      )}
    </div>
  );
};
